import {
  type Control,
  type FieldValues,
  type Path,
  type UseFormRegister,
  useWatch,
} from "react-hook-form";

import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";

import { CUSTOMER_FACING_DESCRIPTION_MAX } from "../constants/campaign-name";
import { CUSTOMER_FACING_COPY } from "../constants/messages";

const FIELD_ID = "customerFacingDescription";
const HELP_ID = "customerFacingDescription-help";
const ERROR_ID = "customerFacingDescription-error";

interface CustomerFacingDescriptionFieldProps<T extends FieldValues> {
  control: Control<T>;
  register: UseFormRegister<T>;
  errorMessage?: string;
}

export function CustomerFacingDescriptionField<T extends FieldValues>({
  control,
  register,
  errorMessage,
}: CustomerFacingDescriptionFieldProps<T>): JSX.Element {
  const name = "mechanicConfig.presentation.customerFacingDescription" as Path<T>;
  const value: unknown = useWatch({ control, name });
  const length: number = typeof value === "string" ? value.length : 0;
  const describedBy: string = errorMessage ? `${HELP_ID} ${ERROR_ID}` : HELP_ID;

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label htmlFor={FIELD_ID}>{CUSTOMER_FACING_COPY.descriptionLabel}</Label>
        <span className="text-xs text-gray-500">
          {length}/{CUSTOMER_FACING_DESCRIPTION_MAX}
        </span>
      </div>
      <Textarea
        id={FIELD_ID}
        rows={2}
        placeholder={CUSTOMER_FACING_COPY.descriptionPlaceholder}
        maxLength={CUSTOMER_FACING_DESCRIPTION_MAX}
        aria-describedby={describedBy}
        aria-invalid={Boolean(errorMessage)}
        {...register(name)}
      />
      <p id={HELP_ID} className="mt-1 text-xs text-gray-500">
        {CUSTOMER_FACING_COPY.descriptionHelp}
      </p>
      {errorMessage && (
        <p id={ERROR_ID} className="mt-1 text-sm text-red-500">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
